import circular from '@/helpers/circular';
import api from '@/store/helpers/api';

const initialState = {
  loading: false,
  enabled: false,
  isCreator: false,
  session: '',
  cardsTeams: -1,
  cardsTeam: -1,
  cardsScores: [],
  rondoTeams: -1,
  rondoTeam: -1,
  rondoScores: [],
  rondoCurrentTeam: -1,
  // This is just to ensure that inner scores are read. Don't delete it.
  dummyChange: true,
};

const getters = {
  score: (state) => (game) => {
    const scores = state[`${game}Scores`];
    const teams = state[`${game}Teams`];

    if (!teams) {
      return '0';
    }
    scores.splice(teams);

    // This is due to vue can't watch object props change (js ref vs value, you know)
    if (state.dummyChange) {
      return scores.join(',');
    }
    return scores.join(',');
  },
  rondoCurrentTeam(state) {
    return state.rondoCurrentTeam + 1;
  },
};

const actions = {
  create({ commit }) {
    commit('LOADSTART');
    return api.post('/class-mode')
      .then((resp) => {
        commit('CREATE');
        commit('ENABLE', resp.data);
      })
      .finally(() => commit('LOADEND'));
  },
  enable({ commit, rootState }, session) {
    commit('LOADSTART');
    return api.get(`/class-mode/${session}`)
      .then((resp) => {
        commit('ENABLE', resp);

        if (rootState.session.id === resp.creator) {
          commit('CREATE');
        }
      })
      .finally(() => commit('LOADEND'));
  },
  disable({ commit }) {
    commit('DISABLE');
  },
  cardsTeams({ commit, state }, teams) {
    commit('CARDS_TEAMS', teams);
    commit('LOADSTART');
    return api.post('/class-mode', {
      id: state.session,
      tinderTeams: teams,
    }).finally(() => commit('LOADEND'));
  },
  rondoTeams({ commit, state }, teams) {
    commit('RONDO_TEAMS', teams);
    commit('LOADSTART');
    return api.post('/class-mode', {
      id: state.session,
      rondoTeams: teams,
    }).finally(() => commit('LOADEND'));
  },
  selectTeam({ commit }, { game, team }) {
    commit('SELECT_TEAM', { game, team });
  },
  setScores({ commit }, { game, scores }) {
    commit('SET_SCORES', { game, scores });
  },
  answer({ commit, state }, {
    success,
    points,
    game,
    gameState,
    currentChar,
  }) {
    if (game === 'cards' && state.isCreator && state.cardsTeams > 1) {
      return false;
    }

    if (success) {
      commit('ADD_SCORE', { game, points });
    }

    let team;

    switch (game) {
      case 'cards':
        team = state.cardsTeam - 1;
        return api.post('tinder/class-mode/score', {
          id: state.session,
          team,
          score: state.cardsScores[team] || 0,
        });

      case 'rondo':
        team = state.rondoCurrentTeam;
        if (!success) {
          commit('RONDO_CHANGE');
        }
        return api.post('rondo/class-mode', {
          team,
          currentChar,
          state: gameState,
          id: state.session,
          score: state.rondoScores[team] || 0,
        });

      default:
        return true;
    }
  },
  rondoSelectTeam({ commit }, team) {
    commit('RONDO_CHANGE', team);
  },
  rondoChange({ commit, state }, currentChar) {
    commit('RONDO_CHANGE');
    return api.post('rondo/class-mode', {
      currentChar,
      id: state.session,
      currentTeam: state.rondoCurrentTeam,
    });
  },
  rondoChangeChar({ state }, currentChar) {
    return api.post('rondo/class-mode', {
      currentChar,
      id: state.session,
    });
  },
  rondoSetState({ state }, data) {
    return api.post('rondo/class-mode', {
      id: state.session,
      state: data,
    });
  },
};

const mutations = {
  LOADSTART(state) {
    state.loading = true;
  },
  LOADEND(state) {
    state.loading = false;
  },
  CREATE(state) {
    state.isCreator = true;
  },
  ENABLE(state, session) {
    state.enabled = true;
    state.session = session.id;
    if (session.tinderTeams) {
      state.cardsTeams = session.tinderTeams;
    }
    if (session.rondoTeams) {
      state.rondoTeams = session.rondoTeams;
    }
  },
  CARDS_TEAMS(state, teams) {
    state.cardsTeams = teams;
  },
  RONDO_TEAMS(state, teams) {
    state.rondoTeams = teams;
  },
  SELECT_TEAM(state, { game, team }) {
    state[`${game}Team`] = team;
  },
  RONDO_CHANGE(state, team) {
    if (typeof team === 'number') {
      state.rondoCurrentTeam = team;
      return team;
    }

    if (state.rondoCurrentTeam === -1) {
      // On first try, select random team
      state.rondoCurrentTeam = Math.floor(Math.random() * 2);
    } else {
      // Other case, just change it.
      const teams = [...Array(state.rondoTeams).keys()];
      state.rondoCurrentTeam = circular.next(teams, state.rondoCurrentTeam);
    }
    return team;
  },
  SET_SCORES(state, { game, scores }) {
    if (!game || !scores) {
      return false;
    }

    let score = scores;
    if (game === 'cards') {
      score = scores.slice(0, state.cardsTeams);
    }

    state[`${game}Scores`] = score;
    return score;
  },
  ADD_SCORE(state, { game }) {
    const scores = state[`${game}Scores`];
    let team = state[`${game}Team`] - 1;

    if (game === 'rondo') {
      team = state[`${game}CurrentTeam`];
    }

    // This is to force recompute the score getter
    state.dummyChange = !state.dummyChange;

    if (!scores[team]) {
      scores[team] = 1;
    } else {
      scores[team] += 1;
    }
  },
  DISABLE(state) {
    state.enabled = false;
    state.session = '';
    state.cardsTeams = -1;
    state.cardsTeam = -1;
    state.cardsScores = [];
    state.rondoTeam = -1;
    state.rondoScores = [];
    state.rondoCurrentTeam = -1;
  },
};

// Export store
export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
